import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface CoinBalance {
    streakDays: string;
    totalCoins: number;
    weekNumber: number;
    turn_on_web_notification: string;
    bindEmail: string;
    yt: string;
    fb: string;
    insta: string;
    frindShare: string;
    is10: string;
    is20: string;
    welcomeBonus: string;
    mobileDt: string;
    episodes: string;
    watchDuration: number;
    watchDurationDate: string;    // Date (YYYY-MM-DD) when watch duration was last recorded
    lastCheckinTime: string;      // ISO timestamp from API's mobile_dt
    nextEligibleTime: string;     // lastCheckin + 24hrs
    streakExpiryTime: string;     // lastCheckin + 48hrs
    serverTime: string;           // Current server time from latest API call
}

interface CoinState {
    coinBalance: CoinBalance;
    setCoinBalance: (partial: Partial<CoinBalance>) => void;
    replaceCoinBalance: (next: CoinBalance) => void;
    logoutClearCoinStore: () => void;
    skipRehydrate: boolean;
    setSkipRehydrate: (skip: boolean) => void;
}



const initialCoinBalance: CoinBalance = {
    streakDays: "0",
    episodes: "",
    totalCoins: 0,
    weekNumber: 1,
    turn_on_web_notification: "0",
    bindEmail: "0",
    yt: "0",
    fb: "0",
    insta: "0",
    frindShare: "0",
    is10: "0",
    is20: "0",
    welcomeBonus: "0",
    mobileDt: "0",
    watchDuration: 0,
    watchDurationDate: "",
    lastCheckinTime: "",
    nextEligibleTime: "",
    streakExpiryTime: "",
    serverTime: ""
};

// Custom storage that only persists when user is logged in
const createGuardedStorage = () => {
    return createJSONStorage<CoinState>(() => ({
        getItem: (name) => {
            if (typeof window === 'undefined') return null;
            try {
                // Check if user is logged in before allowing read
                const userStore = window.localStorage.getItem('_user');
                if (!userStore) {
                    // No user logged in, don't read coin store
                    return null;
                }
                const parsed = JSON.parse(userStore);
                if (!parsed?.state?.user) {
                    // No user in state, don't read
                    return null;
                }
                
                const value = window.localStorage.getItem(name);
                return value;
            } catch {
                return null;
            }
        },
        setItem: (name, value) => {
            if (typeof window === 'undefined') return;
            // Check if user is logged in before allowing persist
            try {
                const userStore = window.localStorage.getItem('_user');
                if (!userStore) {
                    // No user logged in, don't persist coin store
                    return;
                }
                const parsed = JSON.parse(userStore);
                if (!parsed?.state?.user) {
                    // No user in state, don't persist
                    return;
                }
                window.localStorage.setItem(name, value);
            } catch {
                // If any error, don't persist
                return;
            }
        },
        removeItem: (name) => {
            if (typeof window === 'undefined') return;
            try {
                window.localStorage.removeItem(name);
            } catch {}
        }
    }));
};

export const useCoinStore = create<CoinState>()(
    persist<CoinState>(
        (set, get) => ({
            coinBalance: { ...initialCoinBalance },
            skipRehydrate: false,
            setCoinBalance: (partial) => set({ coinBalance: { ...get().coinBalance, ...partial } }),
            replaceCoinBalance: (next) => set({ coinBalance: { ...next } }),
            setSkipRehydrate: (skip) => set({ skipRehydrate: skip }),
            logoutClearCoinStore: () => {
                // Remove from localStorage FIRST to prevent persist from writing back
                if (typeof window !== 'undefined') {
                    try {
                        window.localStorage.removeItem('_coinstore');
                    } catch {}
                }
                // Then set state to initial values
                set({ coinBalance: { ...initialCoinBalance }, skipRehydrate: false });
            },
        }), { 
            name: '_coinstore',
            storage: createGuardedStorage(),
            onRehydrateStorage: () => (state) => {
                // Skip rehydration if a fresh API fetch is in progress
                if (state?.skipRehydrate) {
                    return;
                }
                // Don't clear on rehydration - keep persisted data intact
                // Only clear on explicit logout via logoutClearCoinStore()
            }
        }
    )
);


interface BonusPopupState {
    showBonusPopup: boolean;
    show: () => void;
    hide: () => void;
    clear: () => void; // aggressive clear: memory + localStorage
}



export const useBonusPopupStore = create<BonusPopupState>()(
    persist<BonusPopupState>(
        (set) => ({
            showBonusPopup: false,
            show: () => set({ showBonusPopup: true }),
            hide: () => set({ showBonusPopup: false }),
            clear: () => {
                set({ showBonusPopup: false });
                if (typeof window !== 'undefined') {
                    try {
                        window.localStorage.removeItem('_bonusmodal');
                    } catch {}
                }
            },
        }), { name: '_bonusmodal' }
    )
);

export function logoutClearAllRewardStores() {
    try { useCoinStore.getState().logoutClearCoinStore(); } catch {}
    try { useBonusPopupStore.getState().clear(); } catch {}
    // Clear server time and related checkin time tracking
    if (typeof window !== 'undefined') {
        try {
            localStorage.removeItem('_sst');        // Server time
            localStorage.removeItem('_sst_sync');   // Last sync timestamp
        } catch {}
    }
}